import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../api/generated/core/ApiError';
import { ShipmentLabelRequest } from '../../api/generated/models/ShipmentLabelRequest';
import { ShipmentTrackerSettingsRequest } from '../../api/generated/models/ShipmentTrackerSettingsRequest';
import type { ShipmentLabelView } from '../../api/generated/models/ShipmentLabelView';
import type { ShipmentTransportProfileView } from '../../api/generated/models/ShipmentTransportProfileView';
import ArrowBack from '../../components/ArrowBack';
import LabelModal from '../../components/shipment-tracker/LabelModal';
import TransportProfileModal from '../../components/shipment-tracker/TransportProfileModal';
import DiscardChangesModal from '../../components/shipment-tracker/DiscardChangesModal';
import ShipmentLabelChip from '../../components/shipment-tracker/ShipmentLabelChip';
import Modal from '../../components/ui/Modal';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useShipmentTrackerPermissions } from '../../hooks/useShipmentTrackerPermissions';
import {
  createCustomTransportProfile,
  createShipmentLabel,
  deleteCustomTransportProfile,
  deleteShipmentLabel,
  fetchDropdowns,
  fetchShipmentLabels,
  fetchStatusBoard,
  saveShipmentTrackerSettings,
  updateCustomTransportProfile,
  updateShipmentLabel,
} from '../../store/slices/shipmentTrackerSlice';
import { addNotification } from '../../store/slices/notificationSlice';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg';

type SettingsTab = 'general' | 'transport-profiles' | 'labels';

const TABS: SettingsTab[] = ['general', 'transport-profiles', 'labels'];

const ShipmentTrackerSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { canWrite } = useShipmentTrackerPermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = (searchParams.get('tab') as SettingsTab) || 'general';
  const setTab = (next: SettingsTab) => setSearchParams({ tab: next });

  const settings = useAppSelector((s) => s.shipmentTracker.settings.data);
  const settingsSaving = useAppSelector((s) => s.shipmentTracker.settings.saving);
  const labels = useAppSelector((s) => s.shipmentTracker.labels.items);
  const transportProfiles = useAppSelector(
    (s) => s.shipmentTracker.dropdowns.transportProfiles,
  );

  const [generalForm, setGeneralForm] = useState<ShipmentTrackerSettingsRequest | null>(
    null,
  );

  useEffect(() => {
    if (settings) {
      setGeneralForm({
        defaultCurrency: settings.defaultCurrency as ShipmentTrackerSettingsRequest.defaultCurrency,
        units: settings.units as ShipmentTrackerSettingsRequest.units,
        dateFormat: settings.dateFormat as ShipmentTrackerSettingsRequest.dateFormat,
        timezone: settings.timezone as ShipmentTrackerSettingsRequest.timezone,
      });
    }
  }, [settings]);

  const [labelModal, setLabelModal] = useState<
    { mode: 'create' | 'edit'; label?: ShipmentLabelView } | null
  >(null);
  const [labelError, setLabelError] = useState<string | null>(null);
  const [labelSaving, setLabelSaving] = useState(false);

  const [transportModal, setTransportModal] = useState<
    { mode: 'create' | 'edit'; profile?: ShipmentTransportProfileView } | null
  >(null);
  const [transportError, setTransportError] = useState<string | null>(null);
  const [transportSaving, setTransportSaving] = useState(false);
  const [discardTransport, setDiscardTransport] = useState(false);

  const [deleteLabelTarget, setDeleteLabelTarget] = useState<ShipmentLabelView | null>(
    null,
  );
  const [deleteTransportTarget, setDeleteTransportTarget] =
    useState<ShipmentTransportProfileView | null>(null);

  const standardProfiles = useMemo(
    () => transportProfiles.filter((p) => !p.isCustom),
    [transportProfiles],
  );
  const customProfiles = useMemo(
    () => transportProfiles.filter((p) => p.isCustom),
    [transportProfiles],
  );

  const saveGeneral = async () => {
    if (!generalForm || !canWrite) return;
    try {
      await dispatch(saveShipmentTrackerSettings(generalForm)).unwrap();
      dispatch(
        addNotification({
          type: 'success',
          title: t('success'),
          message: t('shipment_settings_saved'),
        }),
      );
    } catch {
      /* thunk notifies */
    }
  };

  const handleLabelSave = async (payload: {
    name: string;
    color: ShipmentLabelRequest.color;
  }) => {
    if (!labelModal) return;
    setLabelSaving(true);
    setLabelError(null);
    try {
      if (labelModal.mode === 'create') {
        await dispatch(
          createShipmentLabel({
            name: payload.name,
            color: payload.color,
          }),
        ).unwrap();
      } else if (labelModal.label) {
        await dispatch(
          updateShipmentLabel({
            id: labelModal.label.id,
            data: { name: payload.name, color: payload.color },
          }),
        ).unwrap();
      }
      setLabelModal(null);
      dispatch(fetchShipmentLabels());
      dispatch(fetchStatusBoard());
    } catch (e) {
      setLabelError(e instanceof ApiError ? e.body?.error ?? t('error') : t('error'));
    } finally {
      setLabelSaving(false);
    }
  };

  const handleTransportSave = async (payload: {
    name: string;
    details: string | null;
  }) => {
    if (!transportModal) return;
    setTransportSaving(true);
    setTransportError(null);
    try {
      if (transportModal.mode === 'create') {
        await dispatch(
          createCustomTransportProfile({
            name: payload.name,
            details: payload.details,
          }),
        ).unwrap();
      } else if (transportModal.profile) {
        await dispatch(
          updateCustomTransportProfile({
            id: transportModal.profile.id,
            data: { name: payload.name, details: payload.details },
          }),
        ).unwrap();
      }
      setTransportModal(null);
      dispatch(fetchDropdowns());
    } catch (e) {
      const msg = e instanceof ApiError ? e.body?.error ?? t('error') : t('error');
      setTransportError(msg);
    } finally {
      setTransportSaving(false);
    }
  };

  const confirmDeleteLabel = useCallback(async () => {
    if (!deleteLabelTarget) return;
    try {
      await dispatch(deleteShipmentLabel(deleteLabelTarget.id)).unwrap();
      setDeleteLabelTarget(null);
      dispatch(fetchStatusBoard());
    } catch {
      /* notified */
    }
  }, [deleteLabelTarget, dispatch]);

  const confirmDeleteTransport = useCallback(async () => {
    if (!deleteTransportTarget) return;
    try {
      await dispatch(deleteCustomTransportProfile(deleteTransportTarget.id)).unwrap();
      setDeleteTransportTarget(null);
      dispatch(fetchDropdowns());
    } catch {
      /* notified */
    }
  }, [deleteTransportTarget, dispatch]);

  return (
    <div className="page shipment-item-form-page shipment-settings-page">
      <div className="page-header">
        <ArrowBack onClick={() => navigate('/features/shipment-tracker')} />
        <h1>{t('shipment_settings_title')}</h1>
      </div>

      <p className="shipment-settings-page__subtitle">{t('shipment_settings_subtitle')}</p>

      <div className="shipment-detail-tabs" role="tablist">
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`tab-btn${tab === id ? ' tab-btn--active' : ''}`}
            onClick={() => setTab(id)}
          >
            {t(`shipment_settings_tab_${id}`)}
          </button>
        ))}
      </div>

      {tab === 'general' && generalForm && (
        <div className="form-card">
          <h2 className="form-card__title">{t('shipment_settings_tab_general')}</h2>
          <p className="shipment-settings-card__intro">{t('shipment_settings_general_intro')}</p>
          <label className="form-label">
            {t('shipment_settings_default_currency')}
            <select
              className="input"
              disabled={!canWrite}
              value={generalForm.defaultCurrency}
              onChange={(e) =>
                setGeneralForm({
                  ...generalForm,
                  defaultCurrency: e.target
                    .value as ShipmentTrackerSettingsRequest.defaultCurrency,
                })
              }
            >
              <option value={ShipmentTrackerSettingsRequest.defaultCurrency.EUR}>
                {t('shipment_settings_currency_eur')}
              </option>
              <option value={ShipmentTrackerSettingsRequest.defaultCurrency.USD}>
                {t('shipment_settings_currency_usd')}
              </option>
            </select>
          </label>
          <label className="form-label">
            {t('shipment_settings_units')}
            <select
              className="input"
              disabled={!canWrite}
              value={generalForm.units}
              onChange={(e) =>
                setGeneralForm({
                  ...generalForm,
                  units: e.target.value as ShipmentTrackerSettingsRequest.units,
                })
              }
            >
              <option value={ShipmentTrackerSettingsRequest.units.METRIC}>
                {t('shipment_settings_units_metric')}
              </option>
              <option value={ShipmentTrackerSettingsRequest.units.IMPERIAL}>
                {t('shipment_settings_units_imperial')}
              </option>
            </select>
          </label>
          <label className="form-label">
            {t('shipment_settings_date_format')}
            <select
              className="input"
              disabled={!canWrite}
              value={generalForm.dateFormat}
              onChange={(e) =>
                setGeneralForm({
                  ...generalForm,
                  dateFormat: e.target.value as ShipmentTrackerSettingsRequest.dateFormat,
                })
              }
            >
              <option value={ShipmentTrackerSettingsRequest.dateFormat.DD_MM_YYYY}>
                DD/MM/YYYY
              </option>
              <option value={ShipmentTrackerSettingsRequest.dateFormat.MM_DD_YYYY}>
                MM/DD/YYYY
              </option>
              <option value={ShipmentTrackerSettingsRequest.dateFormat.YYYY_MM_DD}>
                YYYY-MM-DD
              </option>
            </select>
          </label>
          <label className="form-label">
            {t('shipment_settings_timezone')}
            <select
              className="input"
              disabled={!canWrite}
              value={generalForm.timezone}
              onChange={(e) =>
                setGeneralForm({
                  ...generalForm,
                  timezone: e.target.value as ShipmentTrackerSettingsRequest.timezone,
                })
              }
            >
              <option value={ShipmentTrackerSettingsRequest.timezone.EUROPE_BERLIN}>
                Europe/Berlin
              </option>
              <option value={ShipmentTrackerSettingsRequest.timezone.EUROPE_LONDON}>
                Europe/London
              </option>
              <option value={ShipmentTrackerSettingsRequest.timezone.AMERICA_NEW_YORK}>
                America/New York (ET)
              </option>
              <option value={ShipmentTrackerSettingsRequest.timezone.UTC}>UTC</option>
            </select>
          </label>
          {canWrite && (
            <div className="form-footer shipment-settings-card__footer">
              <button
                type="button"
                className="btn btn-primary btn-with-icon"
                disabled={settingsSaving}
                onClick={saveGeneral}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17 21v-8H7v8M7 3v5h8"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinejoin="round"
                  />
                </svg>
                {t('shipment_settings_save')}
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'transport-profiles' && (
        <div className="form-card shipment-settings-list">
          <h2 className="form-card__title">{t('shipment_settings_transport_heading')}</h2>
          <p className="shipment-settings-card__intro">{t('shipment_settings_transport_intro')}</p>
          {[...standardProfiles, ...customProfiles].map((p) => (
            <div key={p.id} className="shipment-settings-list__row">
              <div>
                <div className="shipment-settings-list__title">{p.mode}</div>
                <div className="shipment-settings-list__sub">
                  {p.isCustom
                    ? p.details || t('shipment_transport_custom_subtitle')
                    : `${p.capacityLabel} · ${p.carrier}`}
                </div>
              </div>
              <span
                className={`shipment-settings-badge${
                  p.isCustom ? ' shipment-settings-badge--custom' : ' shipment-settings-badge--standard'
                }`}
              >
                {p.isCustom ? t('shipment_transport_badge_custom') : t('shipment_transport_badge_standard')}
              </span>
              {p.isCustom && canWrite && (
                <div className="shipment-settings-list__actions">
                  <button
                    type="button"
                    className="btn-icon"
                    aria-label={t('edit')}
                    onClick={() =>
                      setTransportModal({ mode: 'edit', profile: p })
                    }
                  >
                    <EditIcon />
                  </button>
                  <button
                    type="button"
                    className="btn-icon btn-icon--danger"
                    aria-label={t('delete')}
                    onClick={() => setDeleteTransportTarget(p)}
                  >
                    <DeleteIcon />
                  </button>
                </div>
              )}
            </div>
          ))}
          {canWrite && (
            <div className="form-footer shipment-settings-card__footer shipment-settings-card__footer--start">
              <button
                type="button"
                className="btn btn-primary btn-with-icon"
                onClick={() => setTransportModal({ mode: 'create' })}
              >
                <PlusIcon aria-hidden /> {t('shipment_transport_add_btn')}
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'labels' && (
        <div className="form-card shipment-settings-list">
          <h2 className="form-card__title">{t('shipment_settings_tab_labels')}</h2>
          <p className="shipment-settings-card__intro">{t('shipment_settings_labels_intro')}</p>
          {labels.map((l) => (
            <div key={l.id} className="shipment-settings-list__row shipment-settings-list__row--label">
              <div className="shipment-settings-label-row__main">
                <ShipmentLabelChip label={l} />
                <span className="shipment-settings-label-row__usage">
                  {(l.usageCount ?? 0) > 0
                    ? t('shipment_label_used_on', { count: l.usageCount ?? 0 })
                    : t('shipment_label_not_used')}
                </span>
              </div>
              {canWrite && (
                <div className="shipment-settings-list__actions">
                  <button
                    type="button"
                    className="btn-icon"
                    aria-label={t('edit')}
                    onClick={() => setLabelModal({ mode: 'edit', label: l })}
                  >
                    <EditIcon />
                  </button>
                  <button
                    type="button"
                    className="btn-icon btn-icon--danger"
                    aria-label={t('delete')}
                    onClick={() => setDeleteLabelTarget(l)}
                  >
                    <DeleteIcon />
                  </button>
                </div>
              )}
            </div>
          ))}
          {canWrite && (
            <div className="form-footer shipment-settings-card__footer shipment-settings-card__footer--start">
              <button
                type="button"
                className="btn btn-primary btn-with-icon"
                onClick={() => setLabelModal({ mode: 'create' })}
              >
                <PlusIcon aria-hidden /> {t('shipment_label_add_btn')}
              </button>
            </div>
          )}
        </div>
      )}

      {labelModal && (
        <LabelModal
          mode={labelModal.mode}
          initial={labelModal.label ?? null}
          onClose={() => setLabelModal(null)}
          onSave={handleLabelSave}
          saving={labelSaving}
          errorMessage={labelError}
        />
      )}

      {transportModal && (
        <TransportProfileModal
          mode={transportModal.mode}
          initial={transportModal.profile ?? null}
          onClose={() => {
            setTransportModal(null);
            setTransportError(null);
          }}
          onRequestClose={() => setDiscardTransport(true)}
          onSave={handleTransportSave}
          saving={transportSaving}
          errorMessage={transportError}
        />
      )}

      {discardTransport && (
        <DiscardChangesModal
          onKeepEditing={() => setDiscardTransport(false)}
          onDiscard={() => {
            setDiscardTransport(false);
            setTransportModal(null);
            setTransportError(null);
          }}
        />
      )}

      {deleteLabelTarget && (
        <Modal
          isOpen
          onClose={() => setDeleteLabelTarget(null)}
          title={t('shipment_label_delete_title', { name: deleteLabelTarget.name })}
          hideCloseButton
          className="shipment-tracker-modal shipment-tracker-modal--confirm"
        >
          <p>{t('shipment_label_delete_body', { count: deleteLabelTarget.usageCount ?? 0 })}</p>
          <p>{t('shipment_label_delete_body_undo')}</p>
          <div className="shipment-tracker-modal__footer">
            <button
              type="button"
              className="shipment-tracker-modal__btn shipment-tracker-modal__btn--secondary"
              onClick={() => setDeleteLabelTarget(null)}
            >
              {t('no')}
            </button>
            <button
              type="button"
              className="shipment-tracker-modal__btn shipment-tracker-modal__btn--destructive"
              onClick={confirmDeleteLabel}
            >
              {t('shipment_label_delete_confirm_yes')}
            </button>
          </div>
        </Modal>
      )}

      {deleteTransportTarget && (
        <Modal
          isOpen
          onClose={() => setDeleteTransportTarget(null)}
          title={t('shipment_transport_delete_title')}
          hideCloseButton
          className="shipment-tracker-modal shipment-tracker-modal--confirm"
        >
          <p>
            {t('shipment_transport_delete_body', {
              name: deleteTransportTarget.mode,
            })}
          </p>
          <div className="shipment-settings-modal-warn">
            {t('shipment_transport_delete_warn', {
              count: deleteTransportTarget.usageCount ?? 0,
            })}
          </div>
          <div className="shipment-tracker-modal__footer">
            <button
              type="button"
              className="shipment-tracker-modal__btn shipment-tracker-modal__btn--secondary"
              onClick={() => setDeleteTransportTarget(null)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="shipment-tracker-modal__btn shipment-tracker-modal__btn--destructive"
              onClick={confirmDeleteTransport}
            >
              {t('shipment_transport_delete_confirm')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ShipmentTrackerSettingsPage;
